import { useQuery } from "@tanstack/react-query";

import { getMeetingInfo } from "@app/analytics/api/meetingReport";
import { ResponseError } from "@app/common/api";
import { SessionInfo } from "@app/common/types";
import { hasRetryableStatus } from "@app/common/utils";
import * as SessionUtils from "@app/common/utils/sessionUtils";

interface QueryOpts {
  enabled: boolean;
  refetchOnInterval?: boolean;
}

const fetchSessionInfo = async (sessionId?: string): Promise<SessionInfo> => {
  if (!sessionId) {
    throw Error("must provide a sessionId");
  }
  return getMeetingInfo(sessionId);
};

/**
 * Fetches session-level info for a given sessionId, does not refetch on interval
 */
const useSingleSessionInfoQuery = (
  sessionId?: string,
  opts: QueryOpts = { enabled: true, refetchOnInterval: false }
) => {
  const isEnabled = opts?.enabled;

  return useQuery<SessionInfo, ResponseError>({
    queryKey: ["sessionInfo", sessionId],
    queryFn: () => fetchSessionInfo(sessionId),
    enabled: Boolean(sessionId) && isEnabled,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    refetchInterval: (sessionInfoQuery) => {
      if (
        opts?.refetchOnInterval &&
        SessionUtils.isProcessing(sessionInfoQuery.state.data)
      ) {
        // re-fetch every 5 seconds until the session is no longer active/processing
        return 5000;
      }

      return false;
    },
    retry: (failureCount, error: ResponseError) =>
      failureCount < 3 && hasRetryableStatus(error),
  });
};

export default useSingleSessionInfoQuery;
